import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  Target,
  FileText,
  PieChart,
  Activity,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Settings,
  ArrowRight,
  Timer,
  Zap
} from 'lucide-react';

const TimeTrackingReportsLanding = () => {
  const navigate = useNavigate();

  const reportCards = [
    {
      id: 'time-dashboard-reports',
      title: 'Time Dashboard Reports',
      description: 'Comprehensive overview of time tracking activities and performance metrics',
      icon: <BarChart3 size={24} />,
      path: '/projects/time-tracking/reports/dashboard',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Time overview', 'Productivity analysis', 'Progress tracking', 'Status distribution']
    },
    {
      id: 'productivity-reports',
      title: 'Productivity Reports',
      description: 'Team and individual productivity metrics with detailed analytics',
      icon: <TrendingUp size={24} />,
      path: '/projects/time-tracking/reports/productivity',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Productivity metrics', 'Team efficiency', 'Time analysis', 'Performance trends']
    },
    {
      id: 'billing-reports',
      title: 'Billing Reports',
      description: 'Billable hours analysis, cost tracking, and billing performance insights',
      icon: <DollarSign size={24} />,
      path: '/projects/time-tracking/reports/billing',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Billable hours', 'Revenue analysis', 'Cost breakdown', 'Billing efficiency']
    },
    {
      id: 'resource-utilization',
      title: 'Resource Utilization Reports',
      description: 'Team allocation, capacity planning, and resource efficiency analysis',
      icon: <Users size={24} />,
      path: '/projects/time-tracking/reports/resource-utilization',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Team allocation', 'Capacity planning', 'Utilization rates', 'Skill analysis']
    },
    {
      id: 'timesheet-reports',
      title: 'Timesheet Reports',
      description: 'Weekly and monthly timesheet analysis with approval tracking',
      icon: <FileText size={24} />,
      path: '/projects/time-tracking/reports/timesheets',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Timesheet analysis', 'Approval tracking', 'Submission rates', 'Compliance reports']
    },
    {
      id: 'project-time-reports',
      title: 'Project Time Reports',
      description: 'Time allocation across projects with detailed project insights',
      icon: <Target size={24} />,
      path: '/projects/time-tracking/reports/project-time',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Project allocation', 'Time distribution', 'Project efficiency', 'Milestone tracking']
    },
    {
      id: 'analytics-insights',
      title: 'Analytics & Insights',
      description: 'AI-powered time tracking analytics, trend analysis, and predictive insights',
      icon: <PieChart size={24} />,
      path: '/projects/time-tracking/reports/analytics',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Trend analysis', 'Predictive analytics', 'Pattern recognition', 'Anomaly detection']
    },
    {
      id: 'compliance-reports',
      title: 'Compliance Reports',
      description: 'Time tracking compliance, audit trails, and regulatory reporting',
      icon: <CheckCircle size={24} />,
      path: '/projects/time-tracking/reports/compliance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Compliance tracking', 'Audit trails', 'Regulatory reports', 'Policy adherence']
    },
    {
      id: 'custom-reports',
      title: 'Custom Reports',
      description: 'Create and manage custom time tracking reports for specific business needs',
      icon: <Settings size={24} />,
      path: '/projects/time-tracking/reports/custom',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Executive summaries', 'Custom metrics', 'Stakeholder reports', 'Operational insights']
    },
    {
      id: 'automated-reports',
      title: 'Automated Reports',
      description: 'Scheduled and real-time automated time tracking report generation',
      icon: <Zap size={24} />,
      path: '/projects/time-tracking/reports/automated',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Scheduled reports', 'Real-time dashboards', 'Alert reports', 'Summary reports']
    },
    {
      id: 'overtime-reports',
      title: 'Overtime Reports',
      description: 'Overtime tracking, analysis, and compliance reporting',
      icon: <Clock size={24} />,
      path: '/projects/time-tracking/reports/overtime',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Overtime tracking', 'Compliance analysis', 'Cost analysis', 'Policy adherence']
    },
    {
      id: 'attendance-reports',
      title: 'Attendance Reports',
      description: 'Employee attendance, punctuality, and time-off analysis',
      icon: <Calendar size={24} />,
      path: '/projects/time-tracking/reports/attendance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Attendance tracking', 'Punctuality analysis', 'Time-off patterns', 'Absence reports']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Time Tracking Reports"
        description="Generate comprehensive reports and analytics for time tracking data"
      />
      <div className="page-container">

        {/* Time Tracking Reports Cards */}
        <div className="page-content">
          <div className="card">
            <div className="card-header">
              <h3 className="text-modern-title mb-0">Time Tracking Report Types</h3>
              <p className="text-modern-description mb-0">
                Choose a report type to generate comprehensive time tracking insights and analytics
              </p>
            </div>
            <div className="card-body">
              <div className="grid-modern-3">
                {reportCards.map((card) => (
                  <div
                    key={card.id}
                    className="asset-landing-card"
                    onClick={() => handleCardClick(card.path)}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      background: 'white',
                      borderTop: `4px solid var(--gold-primary)`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div className="card-body" style={{ padding: '20px' }}>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div
                          className="report-icon"
                          style={{
                            background: card.iconBg || '#FFF9E6',
                            borderRadius: '10px',
                            width: '40px',
                            height: '40px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: card.iconLine || 'var(--gold-primary)'
                          }}
                        >
                          {card.icon}
                        </div>
                        <ArrowRight size={16} style={{ color: '#6b7280' }} />
                      </div>

                      <h5 className="card-title" style={{
                        color: '#1f2937',
                        fontWeight: '600',
                        marginBottom: '8px',
                        fontSize: '16px',
                        lineHeight: '1.3'
                      }}>
                        {card.title}
                      </h5>

                      <p className="card-text" style={{
                        color: '#6b7280',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        marginBottom: '16px'
                      }}>
                        {card.description}
                      </p>

                      <div className="report-features">
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {card.features.map((feature, index) => (
                            <span
                              key={index}
                              style={{
                                background: '#f3f4f6',
                                color: '#374151',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '500',
                              }}
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TimeTrackingReportsLanding;
